import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Baby, BarChart3, Building2, Calendar, ClipboardList, Clock, FileText, MessageCircle, Package, Pill, Receipt, Scissors, Settings, Shield, Stethoscope } from 'lucide-react';

export function DashboardPage() {
  return (
    <main className="max-w-7xl mx-auto px-4 py-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link
          to="/patients"
          className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow flex items-start gap-4"
        >
          <Users className="h-8 w-8 text-blue-600 flex-shrink-0" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Patients</h2>
            <p className="text-sm text-gray-500 mt-1">Register, search, and manage patient records</p>
          </div>
        </Link>
        <Link
          to="/pending-registrations"
          className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow flex items-start gap-4"
        >
          <ClipboardList className="h-8 w-8 text-orange-600 flex-shrink-0" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Pending Registrations</h2>
            <p className="text-sm text-gray-500 mt-1">Review patient self-registrations</p>
          </div>
        </Link>
        <Link
          to="/appointments"
          className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow flex items-start gap-4"
        >
          <Calendar className="h-8 w-8 text-green-600 flex-shrink-0" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Appointments</h2>
            <p className="text-sm text-gray-500 mt-1">Schedule and manage appointments</p>
          </div>
        </Link>
        <Link
          to="/logbook"
          className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow flex items-start gap-4"
        >
          <Scissors className="h-8 w-8 text-teal-600 flex-shrink-0" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Surgical Logbook</h2>
            <p className="text-sm text-gray-500 mt-1">Procedure summary and case log</p>
          </div>
        </Link>
        <Link
          to="/delivery-logbook"
          className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow flex items-start gap-4"
        >
          <Baby className="h-8 w-8 text-teal-600 flex-shrink-0" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Delivery Logbook</h2>
            <p className="text-sm text-gray-500 mt-1">Vaginal delivery records and case log</p>
          </div>
        </Link>
        <Link
          to="/waiting-list"
          className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow flex items-start gap-4"
        >
          <Clock className="h-8 w-8 text-amber-600 flex-shrink-0" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Waiting List</h2>
            <p className="text-sm text-gray-500 mt-1">Surgery and procedure waiting list</p>
          </div>
        </Link>
        <Link
          to="/whatsapp"
          className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow flex items-start gap-4"
        >
          <MessageCircle className="h-8 w-8 text-green-500 flex-shrink-0" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">WhatsApp</h2>
            <p className="text-sm text-gray-500 mt-1">AI receptionist, booking requests, conversations</p>
          </div>
        </Link>
        <Link
          to="/claims"
          className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow flex items-start gap-4"
        >
          <Building2 className="h-8 w-8 text-indigo-600 flex-shrink-0" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Claims</h2>
            <p className="text-sm text-gray-500 mt-1">Insurance claims, providers, age analysis</p>
          </div>
        </Link>
        <Link
          to="/billing"
          className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow flex items-start gap-4"
        >
          <FileText className="h-8 w-8 text-purple-600 flex-shrink-0" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Billing</h2>
            <p className="text-sm text-gray-500 mt-1">Invoices, payments, and day analysis</p>
          </div>
        </Link>
        <Link
          to="/reports"
          className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow flex items-start gap-4"
        >
          <BarChart3 className="h-8 w-8 text-teal-600 flex-shrink-0" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Reports</h2>
            <p className="text-sm text-gray-500 mt-1">Monthly revenue, Excel export, analytics</p>
          </div>
        </Link>
      </div>

      {/* Settings */}
      <div className="mt-10">
        <div className="flex items-center gap-2 mb-4">
          <Settings className="h-4 w-4 text-gray-400" />
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Settings</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/settings/consumables"
            className="bg-gray-50 border border-gray-200 rounded-lg p-5 hover:bg-white hover:shadow-sm transition-all flex items-start gap-3"
          >
            <Package className="h-6 w-6 text-gray-400 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-gray-700">Consumables</h3>
              <p className="text-xs text-gray-500 mt-0.5">Manage billable consumables registry</p>
            </div>
          </Link>
          <Link
            to="/settings/formulary"
            className="bg-gray-50 border border-gray-200 rounded-lg p-5 hover:bg-white hover:shadow-sm transition-all flex items-start gap-3"
          >
            <Pill className="h-6 w-6 text-gray-400 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-gray-700">Drug Formulary</h3>
              <p className="text-xs text-gray-500 mt-0.5">Manage commonly prescribed medications</p>
            </div>
          </Link>
          <Link
            to="/settings/icd10"
            className="bg-gray-50 border border-gray-200 rounded-lg p-5 hover:bg-white hover:shadow-sm transition-all flex items-start gap-3"
          >
            <Stethoscope className="h-6 w-6 text-gray-400 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-gray-700">ICD-10 Codes</h3>
              <p className="text-xs text-gray-500 mt-0.5">Manage diagnosis codes</p>
            </div>
          </Link>
          <Link
            to="/settings/nhrpl"
            className="bg-gray-50 border border-gray-200 rounded-lg p-5 hover:bg-white hover:shadow-sm transition-all flex items-start gap-3"
          >
            <Receipt className="h-6 w-6 text-gray-400 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-gray-700">NHRPL Codes</h3>
              <p className="text-xs text-gray-500 mt-0.5">Manage procedure codes and tariffs</p>
            </div>
          </Link>
          <Link
            to="/settings/users"
            className="bg-gray-50 border border-gray-200 rounded-lg p-5 hover:bg-white hover:shadow-sm transition-all flex items-start gap-3"
          >
            <Shield className="h-6 w-6 text-gray-400 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-gray-700">User Management</h3>
              <p className="text-xs text-gray-500 mt-0.5">Add, suspend, and manage admin users</p>
            </div>
          </Link>
          <Link
            to="/settings/hospitals"
            className="bg-gray-50 border border-gray-200 rounded-lg p-5 hover:bg-white hover:shadow-sm transition-all flex items-start gap-3"
          >
            <Building2 className="h-6 w-6 text-gray-400 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-gray-700">Hospitals & Venues</h3>
              <p className="text-xs text-gray-500 mt-0.5">Manage consultation locations</p>
            </div>
          </Link>
          <Link
            to="/settings/procedure-templates"
            className="bg-gray-50 border border-gray-200 rounded-lg p-5 hover:bg-white hover:shadow-sm transition-all flex items-start gap-3"
          >
            <ClipboardList className="h-6 w-6 text-gray-400 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-gray-700">Procedure Templates</h3>
              <p className="text-xs text-gray-500 mt-0.5">Reusable clinical note templates</p>
            </div>
          </Link>
        </div>
      </div>
    </main>
  );
}
